import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Coffee, Menu, X, LogOut } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useBrand } from '../context/BrandContext';

const NavigationHeader = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user, logout } = useAuth();
  const { brand } = useBrand();
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);

  const getDashboardRoute = () => {
    if (!user?.role) {
      // User has no role assigned yet - send to onboarding or general dashboard
      return '/dashboard';
    }
    
    switch (user.role) {
      case 'OWNER':
        return '/dashboard';
      case 'BARISTA':
        return '/barista';
      case 'ADMIN':
        return '/admin';
      default:
        // For any other roles or undefined roles, default to main dashboard
        return '/dashboard';
    }
  };

  const navigationItems = [
    { name: t('nav.home'), path: '/' },
    { name: t('nav.blog'), path: '/blog' },
    { name: t('nav.posts'), path: '/posts' },
    { name: t('nav.pricing'), path: '/pricing' },
  ];

  const isCurrentPage = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    if (path.startsWith('/#')) {
      return location.pathname === '/' && location.hash === path.substring(1);
    }
    return location.pathname.startsWith(path);
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-[#1f2937]/95 backdrop-blur-sm border-b border-gray-700/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            {brand.logoUrl ? (
              <img 
                src={brand.logoUrl} 
                alt={brand.name} 
                className="w-8 h-8 object-contain"
              />
            ) : (
              <Coffee className="text-[#a15507] w-8 h-8" />
            )}
            <span className="text-white text-xl font-bold">{brand.name}</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navigationItems.map((item, index) => (
              <Link
                key={index}
                to={item.path}
                className={`transition-colors duration-200 ${
                  isCurrentPage(item.path)
                    ? 'text-[#a15507] font-semibold'
                    : 'text-gray-300 hover:text-white'
                }`}
                onClick={(e) => {
                  if (item.path.startsWith('/#')) {
                    if (location.pathname !== '/') {
                      // Navigate to home first, then scroll
                      navigate('/');
                      setTimeout(() => {
                        document.querySelector(item.path.substring(1))?.scrollIntoView({ behavior: 'smooth' });
                      }, 100);
                    } else {
                      e.preventDefault();
                      document.querySelector(item.path.substring(1))?.scrollIntoView({ behavior: 'smooth' });
                    }
                  }
                }}
              >
                {item.name}
              </Link>
            ))}

            {/* Auth Buttons */}
            {isAuthenticated ? (
              <div className="flex items-center space-x-4">
                <Link
                  to={getDashboardRoute()}
                  className="bg-[#a15507] hover:bg-[#924e06] text-white px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200"
                >
                  {t('landing.hero.myDashboard')}
                </Link>
                <button
                  onClick={logout}
                  className="text-gray-300 hover:text-white transition-colors duration-200 flex items-center gap-1"
                >
                  <LogOut className="w-4 h-4" />
                  {t('landing.hero.logout')}
                </button>
              </div>
            ) : (
              <div className="flex items-center space-x-4">
                <Link
                  to="/login"
                  className="text-gray-300 hover:text-white transition-colors duration-200"
                >
                  {t('landing.hero.signIn')}
                </Link>
                <Link
                  to="/register"
                  className="bg-[#a15507] hover:bg-[#924e06] text-white px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200"
                >
                  {t('landing.hero.startTrial')}
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-gray-300 hover:text-white transition-colors duration-200"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden border-t border-gray-700/50 py-4">
            <div className="space-y-4">
              {navigationItems.map((item, index) => (
                <Link
                  key={index}
                  to={item.path}
                  className={`block transition-colors duration-200 py-2 ${
                    isCurrentPage(item.path)
                      ? 'text-[#a15507] font-semibold'
                      : 'text-gray-300 hover:text-white'
                  }`}
                  onClick={(e) => {
                    if (item.path.startsWith('/#')) {
                      if (location.pathname !== '/') {
                        navigate('/');
                        setTimeout(() => {
                          document.querySelector(item.path.substring(1))?.scrollIntoView({ behavior: 'smooth' });
                        }, 100);
                      } else {
                        e.preventDefault();
                        document.querySelector(item.path.substring(1))?.scrollIntoView({ behavior: 'smooth' });
                      }
                    }
                    setIsMenuOpen(false);
                  }}
                >
                  {item.name}
                </Link>
              ))}

              {/* Mobile Auth Buttons */}
              {isAuthenticated ? (
                <div className="space-y-2 pt-4 border-t border-gray-700/50">
                  <Link
                    to={getDashboardRoute()}
                    className="block bg-[#a15507] hover:bg-[#924e06] text-white px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 text-center"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {t('landing.hero.myDashboard')}
                  </Link>
                  <button
                    onClick={() => {
                      logout();
                      setIsMenuOpen(false);
                    }}
                    className="block w-full text-left text-gray-300 hover:text-white transition-colors duration-200 py-2 flex items-center gap-2"
                  >
                    <LogOut className="w-4 h-4" />
                    {t('landing.hero.logout')}
                  </button>
                </div>
              ) : (
                <div className="space-y-2 pt-4 border-t border-gray-700/50">
                  <Link
                    to="/login"
                    className="block text-gray-300 hover:text-white transition-colors duration-200 py-2"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {t('landing.hero.signIn')}
                  </Link>
                  <Link
                    to="/register"
                    className="block bg-[#a15507] hover:bg-[#924e06] text-white px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 text-center"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {t('landing.hero.startTrial')}
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default NavigationHeader;
